import { Navigate, useNavigate } from 'react-router-dom';

import { getFragment } from '../../../../graphql/client/helpers/get-fragment';
import { type DigitallyReceivedPrescriberOrderInfoFragment } from '../../../../graphql/generated/graphql';
import { useOrderProviders } from '../../../../hooks/use-order-providers';
import { usePrescriberOrder } from '../../../../hooks/use-prescriber-order';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';
import { getRedirectUrlByOrderStatus } from '../../../../util/orders';
import PatientMedicationDetails from '../../order-details/PatientMedicationDetails';
import { ProviderSelection } from '../provider-offers';

export const PrescriptionPage = () => {
    const prescriberOrderCtx = usePrescriberOrder();

    const navigate = useNavigate();

    const autoRedirectUrl = getRedirectUrlByOrderStatus(
        prescriberOrderCtx.order
    );

    const providers = useOrderProviders(prescriberOrderCtx.order);

    if (autoRedirectUrl) {
        return <Navigate to={autoRedirectUrl} />;
    }

    // If there's no automatic redirect, we know this is a valid order
    const prescriberOrder =
        prescriberOrderCtx.order as DigitallyReceivedPrescriberOrderInfoFragment;
    // We also know that `medication` must exist
    const medication = prescriberOrderCtx.medication!;

    const handleOnSelect = (selectedProviderId: string) => {
        navigate(
            getAbsoluteRoute('order.prescriberOrderId.pharmacy.providerId', {
                prescriberOrderId: prescriberOrderCtx.orderId,
                providerId: selectedProviderId,
            })
        );
    };

    const patient = getFragment(prescriberOrder.patient);

    if (!providers) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    return (
        <ProviderSelection
            drxId={prescriberOrder.drxId ?? ''}
            providers={providers}
            medication={medication}
            onSelect={handleOnSelect}
            header={
                <PatientMedicationDetails
                    patientName={patient.fullName}
                    prescriberOrder={prescriberOrder}
                />
            }
        />
    );
};
